import React, { useState, useEffect } from 'react';
import {
  Users,
  MapPin,
  Globe,
  Briefcase,
  Target,
  Search,
  CheckCircle2,
  Users2,
  ChevronRight,
  ArrowRightLeft,
  LayoutGrid,
} from 'lucide-react';
import { salesManagerService } from '../services/sales-manager.service';
import type { TeamMember } from '../types';

type LabelType = 'Team' | 'Region' | 'District' | 'Organization' | 'Campaign';

export const TeamOrganizationManager: React.FC = () => {
  const [agents, setAgents] = useState<TeamMember[]>([]);
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeLabelType, setActiveLabelType] = useState<LabelType>('Team');
  const [labelText, setLabelText] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchAgents = async () => {
    try {
      setIsLoading(true);
      const response = await salesManagerService.getTeamRecruits();
      setAgents(response.recruits);
    } catch (err) {
      console.warn('API fetch failed, falling back to mock agents for assignment testing');
      setAgents([
        {
          id: '1',
          userId: 'u1',
          firstName: 'John',
          lastName: 'Smith',
          email: 'john.smith@example.com',
          phone: '555-0123',
          role: 'AGENT',
          status: 'active',
          salesCount: 45,
          recruitCount: 12,
          currentFunnelStep: 8,
          stepName: 'First Paycheck',
          joinedAt: '2025-01-15T10:00:00Z',
          performance: 'overperforming'
        },
        {
          id: '2',
          userId: 'u2',
          firstName: 'Sarah',
          lastName: 'Johnson',
          email: 'sarah.j@example.com',
          phone: '555-0124',
          role: 'AGENT',
          status: 'inactive',
          salesCount: 12,
          recruitCount: 3,
          currentFunnelStep: 6,
          stepName: 'Ready to Sell',
          joinedAt: '2025-02-20T10:00:00Z',
          performance: 'inactive'
        },
        {
          id: '3',
          userId: 'u3',
          firstName: 'Mike',
          lastName: 'Williams',
          email: 'mike.w@example.com',
          phone: '555-0125',
          role: 'AGENT',
          status: 'active',
          salesCount: 28,
          recruitCount: 5,
          currentFunnelStep: 8,
          stepName: 'First Paycheck',
          joinedAt: '2025-03-01T10:00:00Z',
          performance: 'normal'
        },
        {
          id: '4',
          userId: 'u4',
          firstName: 'Emily',
          lastName: 'Davis',
          email: 'emily.d@example.com',
          phone: '555-0126',
          role: 'AGENT',
          status: 'active',
          salesCount: 32,
          recruitCount: 4,
          currentFunnelStep: 7,
          stepName: 'First Sale',
          joinedAt: '2025-03-10T10:00:00Z',
          performance: 'normal'
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  const toggleSelect = (id: string) => {
    const next = new Set(selectedAgents);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedAgents(next);
  };

  const handleBulkAssign = async () => {
    if (selectedAgents.size === 0 || !labelText) return;

    try {
      setIsAssigning(true);
      await salesManagerService.assignToGroup({
        agentIds: Array.from(selectedAgents),
        label: labelText,
        type: activeLabelType
      });
      setSuccessMessage(`Successfully assigned ${selectedAgents.size} members to ${activeLabelType}: ${labelText}`);
      setSelectedAgents(new Set());
      setLabelText('');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Failed to assign group');
    } finally {
      setIsAssigning(false);
    }
  };

  const labelIcons = {
    Team: Users2,
    Region: Globe,
    District: MapPin,
    Organization: Briefcase,
    Campaign: Target
  };

  const filteredAgents = agents.filter(a => 
    `${a.firstName} ${a.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Organizational Management</h2>
          <p className="text-gray-600">Assign team members to specific regions, districts, or groups</p>
        </div>
        {successMessage && (
          <div className="bg-green-100 border border-green-200 text-green-700 px-4 py-2 rounded-lg flex items-center gap-2 animate-in slide-in-from-top duration-300">
            <CheckCircle2 className="w-5 h-5" />
            {successMessage}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Assignment Controls */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-6">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-blue-600" />
              Assignment Tool
            </h3>
            
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-700">1. Select Label Type</label>
              <div className="grid grid-cols-1 gap-2">
                {(Object.keys(labelIcons) as LabelType[]).map((type) => {
                  const Icon = labelIcons[type];
                  return (
                    <button
                      key={type}
                      onClick={() => setActiveLabelType(type)}
                      className={`flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        activeLabelType === type 
                          ? 'bg-blue-600 text-white shadow-md' 
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {type}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-700">2. Enter {activeLabelType} Name</label>
              <input
                type="text"
                value={labelText}
                onChange={(e) => setLabelText(e.target.value)}
                placeholder={`e.g. North East ${activeLabelType}`}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
              />
            </div>

            <div className="pt-4 border-t">
              <div className="mb-4 text-center">
                <p className="text-sm text-gray-500">Selected Members</p>
                <p className="text-3xl font-black text-blue-600">{selectedAgents.size}</p>
              </div>
              <button
                disabled={selectedAgents.size === 0 || !labelText || isAssigning}
                onClick={handleBulkAssign}
                className="w-full py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 transition-all shadow-lg shadow-blue-100 flex items-center justify-center gap-2"
              >
                {isAssigning ? 'Assigning...' : 'Bulk Assign'}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Member Selector */}
        <div className="lg:col-span-3 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col h-[700px]">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between gap-4 bg-gray-50/50">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search members to assign..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                />
              </div>
              <button 
                onClick={() => setSelectedAgents(new Set(agents.map(a => a.id)))}
                className="text-xs font-bold text-blue-600 hover:underline"
              >
                Select All
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 grid grid-cols-1 md:grid-cols-2 gap-3 content-start">
              {isLoading ? (
                <div className="col-span-full py-20 text-center text-gray-500">Loading members...</div>
              ) : filteredAgents.map((agent) => (
                <div
                  key={agent.id}
                  onClick={() => toggleSelect(agent.id)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex items-center justify-between ${
                    selectedAgents.has(agent.id)
                      ? 'border-blue-500 bg-blue-50 shadow-sm'
                      : 'border-gray-100 hover:border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      selectedAgents.has(agent.id) ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {agent.firstName[0]}{agent.lastName[0]}
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-gray-900 truncate text-sm">
                        {agent.firstName} {agent.lastName}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{agent.email}</p>
                    </div>
                  </div>
                  {selectedAgents.has(agent.id) && (
                    <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">
                {selectedAgents.size} of {agents.length} members selected
              </span>
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => setSelectedAgents(new Set())}
                  className="text-sm font-bold text-gray-400 hover:text-gray-600"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamOrganizationManager;
